/**
 * Πρόχειρο παραγγελίας του PDA (ιδιοκτήτης 7/10/2026: «κάνουν refresh κατά λάθος και χάνουν την
 * παραγγελία»). Ό,τι ΔΕΝ στάλθηκε μένει στη συσκευή (localStorage), ανά κατάστημα και τραπέζι, και
 * ξαναβγαίνει όταν ανοίξει πάλι η παραγγελία του τραπεζιού — μετά από refresh, κλείσιμο της εφαρμογής,
 * «πίσω» του κινητού ή αποσύνδεση. Φεύγει με την αποστολή ή με επιβεβαιωμένη έξοδο (✕ δύο φορές).
 *
 * Μόνο φύλαξη/ανάκτηση: η τιμή κάθε γραμμής είναι αυτή που υπολογίστηκε στην προσθήκη, όπως θα ήταν
 * και στη μνήμη· το payload της αποστολής δεν αλλάζει.
 *
 * Ένα πρόχειρο ισχύει μόνο για την ΙΔΙΑ συνεδρία τραπεζιού (ή για τραπέζι που ήταν ελεύθερο όταν
 * γράφτηκε): αν το τραπέζι έκλεισε και ξανάνοιξε, ήταν άλλοι πελάτες — το πρόχειρο πετιέται. Και
 * λήγει μετά από DRAFT_TTL_MS: κανένα «ξεχασμένο» καλάθι του μεσημεριού στο βραδινό.
 */

export const DRAFT_TTL_MS = 3 * 60 * 60 * 1000;

const keyOf = (storeId: number) => `xentrix_waiter_drafts_${storeId}`;

export interface OrderDraft<L = unknown> {
  v: 1;
  tableId: number;
  tableNumber: string;
  /** Η συνεδρία όταν γράφτηκε· null = το τραπέζι ήταν ελεύθερο. */
  sessionId: number | null;
  guestCount: number;
  savedAt: number;
  lines: L[];
  messages: string[];
}

type DraftMap = Record<string, OrderDraft>;

const isDraft = (d: any): d is OrderDraft =>
  !!d && d.v === 1 && Number.isInteger(d.tableId) && typeof d.tableNumber === 'string'
  && (d.sessionId === null || Number.isInteger(d.sessionId)) && Number.isFinite(d.guestCount)
  && Number.isFinite(d.savedAt) && Array.isArray(d.lines) && d.lines.length > 0
  && Array.isArray(d.messages) && d.messages.every((m: unknown) => typeof m === 'string');

function readMap(storeId: number): DraftMap {
  try {
    const raw = JSON.parse(localStorage.getItem(keyOf(storeId)) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch {
    return {}; // χαλασμένο JSON / αποκλεισμένο storage — σαν να μην υπάρχει πρόχειρο
  }
}

function writeMap(storeId: number, map: DraftMap): void {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(keyOf(storeId));
    else localStorage.setItem(keyOf(storeId), JSON.stringify(map));
  } catch {
    /* χωρίς storage το πρόχειρο ζει μόνο όσο είναι ανοιχτή η οθόνη — όπως πριν */
  }
}

/** Τα ζωντανά πρόχειρα του καταστήματος (νεότερο πρώτο)· τα ληγμένα/άκυρα σβήνονται εδώ. */
export function readOrderDrafts(storeId: number, now = Date.now()): OrderDraft[] {
  const map = readMap(storeId);
  const alive: DraftMap = {};
  for (const [key, draft] of Object.entries(map)) {
    if (isDraft(draft) && now - draft.savedAt < DRAFT_TTL_MS && now >= draft.savedAt) alive[key] = draft;
  }
  if (Object.keys(alive).length !== Object.keys(map).length) writeMap(storeId, alive);
  return Object.values(alive).sort((a, b) => b.savedAt - a.savedAt);
}

/** Το πρόχειρο του τραπεζιού, αν ισχύει για τη συνεδρία που ανοίγει τώρα (βλ. πάνω). */
export function readOrderDraft(
  storeId: number, tableId: number, sessionId: number | null, now = Date.now()
): OrderDraft | null {
  const draft = readOrderDrafts(storeId, now).find((d) => d.tableId === tableId);
  return draft && draftFitsSession(draft, sessionId) ? draft : null;
}

export const draftFitsSession = (draft: OrderDraft, sessionId: number | null): boolean =>
  draft.sessionId === null || draft.sessionId === sessionId;

export function writeOrderDraft(storeId: number, draft: Omit<OrderDraft, 'v' | 'savedAt'>, now = Date.now()): void {
  const map = readMap(storeId);
  map[String(draft.tableId)] = { ...draft, v: 1, savedAt: now };
  writeMap(storeId, map);
}

export function clearOrderDraft(storeId: number, tableId: number): void {
  const map = readMap(storeId);
  if (!(String(tableId) in map)) return;
  delete map[String(tableId)];
  writeMap(storeId, map);
}
